import type { MouseEvent } from 'react';
import { BASE_URL } from '../../config/api-routes';
import type { RankedPromptChoice } from '../../utils/parseRankedAiPromptList';
import { tierFromIndexForBadge } from '../../utils/promptRecommendationBadge';
import type { AppPrompt, PromptUploadStatus } from './promptsV2Types';

const GENERIC_PROMPT_TEXTS = [
  'Can you briefly describe your professional background and experience relevant to your role?',
  'Can you tell me a little about yourself and what drives your career?',
  'What has prepared you best for the kind of opportunities you want next?',
  'What is your proudest professional achievement, and what made it significant?',
  'How do you approach learning new skills or technologies on the job?',
  'Describe a time you faced a difficult challenge at work. How did you handle it?',
  'What does your ideal work environment look like, and why?',
  'How do you manage competing priorities or tight deadlines?',
  'Tell me about a time you worked closely with a team to deliver a project. What was your role?',
  'Why are you looking for a new opportunity at this point in your career?',
  'What type of company culture do you thrive in, and why?',
  'What is one professional skill you are currently working on improving?',
  'How do you handle feedback or constructive criticism from colleagues or managers?',
  'What motivates you most in your day-to-day work?',
  'Where do you see yourself professionally in the next 3 to 5 years?',
];

export const GENERIC_PROMPT_CHOICES: RankedPromptChoice[] = GENERIC_PROMPT_TEXTS.map(
  (text, index) => ({
    text,
    tier: tierFromIndexForBadge(index),
  })
);

export function mediaUrl(url: string | null | undefined) {
  if (!url || url === 'N/A') return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BASE_URL}${url.replace(/^\//, '')}`;
  return `${BASE_URL}${url}`;
}

const UPLOAD_STAGING_PREFIXES = [
  'prompt_upload_jobs_docs_',
  'prompt_upload_jobs_videos_',
  'prompt_docs_',
  'prompt_videos_',
];

/** Readable label for S3 keys that include staging path segments. */
export function friendlyDocumentName(rawName: string): string {
  let name = decodeURIComponent(rawName || 'document').trim();
  for (const prefix of UPLOAD_STAGING_PREFIXES) {
    if (name.toLowerCase().startsWith(prefix.toLowerCase())) {
      name = name.slice(prefix.length);
      break;
    }
  }
  if (!name) return 'Document';
  if (name.length > 44) {
    const dot = name.lastIndexOf('.');
    if (dot > 0 && dot < name.length - 1) {
      const ext = name.slice(dot + 1);
      const stem = name.slice(0, dot);
      return `${stem.slice(0, 36)}….${ext}`;
    }
    return `${name.slice(0, 40)}…`;
  }
  return name;
}

export function docMetaFromUrl(url: string) {
  const clean = url.split('?')[0];
  const rawName = clean.split('/').pop() || 'document';
  const name = friendlyDocumentName(rawName);
  const ext = (name.split('.').pop() || '').toLowerCase();

  let type = 'File';
  let icon = '📎';
  if (ext === 'pdf') {
    type = 'PDF';
    icon = '📕';
  } else if (ext === 'doc' || ext === 'docx') {
    type = 'Word';
    icon = '📘';
  } else if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) {
    type = 'Image';
    icon = '🖼️';
  }

  return { name, type, icon };
}

/** Map API snake_case + camelCase into the shape Prompts UI expects. */
export function normalizeAppPrompt(raw: Record<string, unknown>): AppPrompt {
  const fileUrl = String(raw.fileUrl ?? raw.file_url ?? '').trim();
  const supportedDocumentUrl = String(
    raw.supportedDocumentUrl ?? raw.supported_document_url ?? ''
  ).trim();

  const uploadStatusRaw = String(raw.uploadStatus ?? raw.upload_status ?? 'ready').toLowerCase();
  const uploadStatus: PromptUploadStatus =
    uploadStatusRaw === 'queued' || uploadStatusRaw === 'processing' || uploadStatusRaw === 'failed'
      ? uploadStatusRaw
      : 'ready';

  const visibilityRaw = String(raw.visibility ?? 'public').toLowerCase();
  const visibility =
    visibilityRaw === 'recruiter_only' || visibilityRaw === 'private' ? visibilityRaw : 'public';

  return {
    id: Number(raw.id),
    content: String(raw.content ?? ''),
    description: String(raw.description ?? ''),
    visibility,
    fileUrl: fileUrl || undefined,
    supportedDocumentUrl: supportedDocumentUrl || undefined,
    uploadStatus,
    uploadError: String(raw.uploadError ?? raw.upload_error ?? '').trim(),
    jobTitle: String(raw.jobTitle ?? raw.job_title ?? ''),
    jobIndustry: String(raw.jobIndustry ?? raw.job_industry ?? ''),
    level: String(raw.level ?? ''),
    created_at: raw.created_at as string | undefined,
    updated_at: raw.updated_at as string | undefined,
  };
}

export function normalizeAppPrompts(list: unknown[]): AppPrompt[] {
  if (!Array.isArray(list)) return [];
  return list
    .filter((item) => item && typeof item === 'object')
    .map((item) => normalizeAppPrompt(item as Record<string, unknown>));
}

export function promptUploadStatus(prompt: AppPrompt): PromptUploadStatus {
  return prompt.uploadStatus ?? 'ready';
}

export function isVideoUploadPending(prompt: AppPrompt) {
  const status = promptUploadStatus(prompt);
  return status === 'queued' || status === 'processing';
}

/** True when a video file was just submitted but is not playable yet. */
export function isVideoUploadInProgress(prompt: AppPrompt, submittedVideo?: boolean) {
  if (isVideoUploadPending(prompt)) return true;
  if (!submittedVideo) return false;
  return !hasPromptVideo(prompt);
}

export function hasPromptVideo(prompt: AppPrompt) {
  const url = String(prompt.fileUrl ?? '').trim();
  return Boolean(url && url !== 'N/A');
}

export function hasPromptDescription(prompt: AppPrompt) {
  return Boolean((prompt.description || '').trim());
}

export function hasPromptDocument(prompt: AppPrompt) {
  const url = String(prompt.supportedDocumentUrl ?? '').trim();
  return Boolean(url);
}

/** Video + description only; supporting documents are optional. */
export function promptCompleteness(prompt: AppPrompt) {
  let count = 0;
  if (hasPromptVideo(prompt)) count += 1;
  if (hasPromptDescription(prompt)) count += 1;
  return count;
}

export function promptStatusLabel(prompt: AppPrompt) {
  const count = promptCompleteness(prompt);
  if (count === 0) return 'Incomplete';
  if (count < 2) return 'Partial';
  return 'Complete';
}

export function promptStatusTone(prompt: AppPrompt) {
  const count = promptCompleteness(prompt);
  if (count === 0) {
    return 'border border-slate-200 bg-slate-50 text-slate-600';
  }
  if (count < 2) {
    return 'border border-amber-200/90 bg-amber-50 text-amber-900';
  }
  return 'border border-emerald-200/90 bg-emerald-50 text-emerald-800';
}

/** Human-readable copy listing only the pieces still missing from a prompt. */
export function promptImprovementCopy(prompt: AppPrompt): string | null {
  const missing: string[] = [];
  if (!hasPromptVideo(prompt)) missing.push('a video response');
  if (!hasPromptDescription(prompt)) missing.push('a short description');
  if (missing.length === 0) return null;
  if (missing.length === 1) {
    return `Add ${missing[0]} for a complete response.`;
  }
  if (missing.length === 2) {
    return `Add ${missing[0]} and ${missing[1]} for a complete response.`;
  }
  return `Add ${missing[0]}, ${missing[1]}, and ${missing[2]} for a complete response.`;
}

export function overlayClose(event: MouseEvent<HTMLDivElement>, close: () => void) {
  if (event.target === event.currentTarget) close();
}
